import { useEffect, useState } from "react";
import { Download, Mail, ShieldAlert, Trash2 } from "lucide-react";
import {
  Button, Card, CardContent, CardHeader, CardTitle,
  ConfirmDialog, Input, Label, Separator, Switch,
  LoadingState,
} from "@wa/ui";
import { useT, useI18n } from "../../i18n/I18nProvider";
import { api, ApiError } from "../../lib/api";
import { deleteAccount, type AccountUser } from "../../lib/auth";

interface ConsentRow {
  id: string;
  kind: string;
  granted: boolean;
  version: string;
  createdAt: string;
}
interface PrivacyData {
  consents: Record<string, boolean>;
  consentHistory: ConsentRow[];
  sites: { id: string; name: string; paid: boolean }[];
  deletion: {
    blocked: boolean;
    reason: string | null;
    hasPaidSubscription: boolean;
    gracePeriodDays: number;
    pending: { createdAt: string; effectiveAt: string } | null;
  };
}

const CONSENT_KINDS = ["marketing", "cookies"] as const;

export function PrivacySection({ user, onLogout }: { user: AccountUser; onLogout: () => void }) {
  const t = useT();
  const { locale } = useI18n();
  const [data, setData] = useState<PrivacyData | null>(null);
  const [exporting, setExporting] = useState(false);

  // Deletion
  const [pwd, setPwd] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [delError, setDelError] = useState<string | null>(null);
  // Passwordless flow: confirmation link sent → persistent "check your email" notice.
  const [emailSentTo, setEmailSentTo] = useState<string | null>(null);

  useEffect(() => {
    void api.get<PrivacyData>("/account/privacy").then(setData);
  }, []);

  function fmt(d: string) {
    return new Date(d).toLocaleString(locale === "en" ? "en-GB" : "et-EE", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }

  async function toggleConsent(kind: string, granted: boolean) {
    setData((d) => (d ? { ...d, consents: { ...d.consents, [kind]: granted } } : d));
    await api.post("/account/privacy/consent", { kind, granted });
    setData(await api.get<PrivacyData>("/account/privacy"));
  }

  async function exportData() {
    setExporting(true);
    try {
      const payload = await api.post<unknown>("/account/privacy/export");
      const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `webadmin-andmed-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    setDelError(null);
    try {
      const res = await deleteAccount(user.hasPassword ? pwd : undefined);
      setConfirmOpen(false);
      // Passwordless (Google) user: the deletion did NOT happen - a confirmation link was sent
      // to the email. Show a persistent notice, do NOT log out (the session stays valid).
      if (res.emailConfirmation) {
        setEmailSentTo(res.sentTo ?? user.email);
        return;
      }
      // User with a password: the deletion took effect immediately → log out.
      onLogout();
    } catch (err) {
      const code =
        err instanceof ApiError && err.body && typeof err.body === "object"
          ? (err.body as { error?: string }).error
          : null;
      setDelError(code === "wrong_password" ? t("privacy.wrongPassword") : t("common.error"));
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  }

  if (!data) {
    return (
      <Card>
        <CardContent><LoadingState label={t("common.loading")} /></CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* ── Consents ─────────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t("privacy.consentTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">{t("privacy.consentIntro")}</p>
          {CONSENT_KINDS.map((kind) => (
            <div key={kind} className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-sm font-medium">{t(`privacy.consent.${kind}`)}</div>
                <div className="text-xs text-muted-foreground">{t(`privacy.consentDesc.${kind}`)}</div>
              </div>
              <Switch
                checked={data.consents[kind] ?? false}
                onCheckedChange={(v) => toggleConsent(kind, v)}
                aria-label={t(`privacy.consent.${kind}`)}
              />
            </div>
          ))}

          {data.consentHistory.length > 0 && (
            <>
              <Separator />
              <div>
                <div className="mb-2 text-xs font-medium text-muted-foreground">{t("privacy.consentHistory")}</div>
                <ul className="space-y-1 text-xs text-muted-foreground">
                  {data.consentHistory.slice(0, 8).map((c) => (
                    <li key={c.id} className="flex justify-between gap-3">
                      <span>
                        {t(`privacy.consent.${c.kind}`)} -{" "}
                        <span className={c.granted ? "text-emerald-600 dark:text-emerald-400" : "text-destructive"}>
                          {c.granted ? t("privacy.granted") : t("privacy.revoked")}
                        </span>
                      </span>
                      <span className="shrink-0 tabular-nums">{fmt(c.createdAt)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* ── Data export ────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle>{t("privacy.exportTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">{t("privacy.exportIntro")}</p>
          <Button variant="outline" onClick={exportData} loading={exporting}>
            <Download className="size-4" />
            {t("privacy.exportButton")}
          </Button>
        </CardContent>
      </Card>

      {/* ── Account deletion ──────────────────────────────────────────── */}
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">{t("privacy.deleteTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("privacy.deleteIntro", { days: data.deletion.gracePeriodDays })}
          </p>

          {data.deletion.blocked ? (
            <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/50 p-3 text-sm">
              <ShieldAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">{t("privacy.blockedSuperadmin")}</span>
            </div>
          ) : emailSentTo ? (
            <div className="flex items-start gap-2 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm">
              <Mail className="mt-0.5 size-4 shrink-0 text-primary" />
              <span>{t("privacy.deleteEmailSent", { email: emailSentTo })}</span>
            </div>
          ) : (
            <>
              {data.deletion.hasPaidSubscription && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                  <ShieldAlert className="mt-0.5 size-4 shrink-0 text-amber-600 dark:text-amber-400" />
                  <span>{t("privacy.paidWarning")}</span>
                </div>
              )}

              {user.hasPassword ? (
                <div className="max-w-sm space-y-1.5">
                  <Label htmlFor="del-pwd">{t("privacy.confirmPassword")}</Label>
                  <Input
                    id="del-pwd"
                    type="password"
                    autoComplete="current-password"
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    placeholder="••••••••"
                  />
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">{t("privacy.noPasswordDelete")}</p>
              )}
              {delError && <p className="text-sm text-destructive">{delError}</p>}

              <Button variant="destructive" disabled={user.hasPassword ? !pwd : false} onClick={() => setConfirmOpen(true)}>
                <Trash2 className="size-4" />
                {t("privacy.deleteButton")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={confirmOpen}
        variant="destructive"
        title={t("privacy.deleteConfirmTitle")}
        description={t("privacy.deleteConfirmBody", { days: data.deletion.gracePeriodDays })}
        confirmLabel={t("privacy.deleteButton")}
        cancelLabel={t("common.cancel")}
        loading={deleting}
        onConfirm={doDelete}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
